import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { Check, ListChecks, Minus, ShieldCheck, X, CircleHelp } from 'lucide-react';
import { countBulkScope } from '../../../utils/complianceFilters';

/**
 * BulkComplianceModal
 *
 * Notion/Anthropic-styled modal for the reviewer-side bulk verdict
 * action. Visual language mirrors `DismissActionsModal.jsx` (the dark
 * #111111 surface, pill buttons, custom checkbox, sectioned divider
 * layout) but the file is intentionally self-contained — no shared
 * primitives module — so this surface evolves independently of the
 * deals modal.
 *
 * The component is a pure presentation/state shell. All side effects
 * (cache reads, optimistic merge, batch POSTs) live in
 * `useBulkReviewerDecisions`. We accept:
 *
 *   - `drafts`        the message-draft list from `useReviewerCampaign`
 *   - `contacts`      the contact list (used for live preservation count)
 *   - `onApply`       async ({ verdict, note, configIds }) → totals
 *   - `onComplete`    optional callback fired with the totals after apply
 *
 * Apply UX is fire-and-forget: the modal closes the instant Apply is
 * clicked, the parent's optimistic merge flips chips synchronously, and
 * the toast (rendered by the parent from `onComplete`) summarizes the
 * eventual result.
 */

// ── Local style tokens ──────────────────────────────────────────────────
//
// Borrowed from DismissActionsModal's visual language but redeclared
// locally so the two modals can drift independently. Keep these tight
// and readable; widening them is a UX decision, not a refactor.

const PRIMARY_BTN = {
  bg: '#fafafa',
  color: '#0a0a0a',
  borderRadius: 'full',
  fontWeight: '600',
  fontSize: '13px',
  h: '36px',
  _hover: { opacity: 0.85 },
  _active: { opacity: 0.7 },
};

const OUTLINE_BTN = {
  bg: 'transparent',
  borderRadius: 'full',
  fontWeight: '500',
  fontSize: '13px',
  h: '36px',
  border: '1px solid rgba(255,255,255,0.15)',
  color: 'rgba(255,255,255,0.6)',
  _hover: {
    borderColor: 'rgba(255,255,255,0.3)',
    color: 'rgba(255,255,255,0.9)',
    bg: 'rgba(255,255,255,0.04)',
  },
};

const NOTE_MAX_LEN = 1000;
const NOTE_MIN_LEN_FOR_NON_COMPLIANT = 3; // mirrors backend constant

// ── CustomCheckbox ──────────────────────────────────────────────────────

const CustomCheckbox = ({ checked, indeterminate = false, onClick }) => (
  <Box
    as="span"
    w="16px"
    h="16px"
    flexShrink={0}
    borderRadius="4px"
    border="1.5px solid"
    borderColor={checked || indeterminate ? '#fafafa' : 'rgba(255,255,255,0.3)'}
    bg={checked || indeterminate ? '#fafafa' : 'transparent'}
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    transition="all 0.12s"
    cursor="pointer"
    onClick={(e) => {
      e.stopPropagation();
      onClick?.();
    }}
  >
    {indeterminate && !checked ? (
      <Minus size={10} strokeWidth={2.5} color="#0a0a0a" />
    ) : checked ? (
      <Check size={10} strokeWidth={2.5} color="#0a0a0a" />
    ) : null}
  </Box>
);

// ── Verdict pill (segmented control feel) ───────────────────────────────

const VerdictPill = ({ active, onClick, icon: Icon, label, accent }) => (
  <Box
    as="button"
    onClick={onClick}
    flex={1}
    px={3}
    py={2.5}
    borderRadius="8px"
    border="1px solid"
    borderColor={active ? accent.border : 'rgba(255,255,255,0.08)'}
    bg={active ? accent.bg : 'transparent'}
    color={active ? accent.color : 'rgba(255,255,255,0.6)'}
    transition="all 0.12s"
    cursor="pointer"
    _hover={{
      borderColor: active ? accent.border : 'rgba(255,255,255,0.18)',
      bg: active ? accent.bg : 'rgba(255,255,255,0.03)',
    }}
  >
    <HStack spacing={2} justify="center">
      <Icon size={14} strokeWidth={2} />
      <Text fontSize="13px" fontWeight="600">
        {label}
      </Text>
    </HStack>
  </Box>
);

const ACCENT_COMPLIANT = {
  bg: 'rgba(52,211,153,0.12)',
  color: '#34D399',
  border: 'rgba(52,211,153,0.42)',
};
const ACCENT_NON_COMPLIANT = {
  bg: 'rgba(239,68,68,0.12)',
  color: '#EF4444',
  border: 'rgba(239,68,68,0.42)',
};
const ACCENT_NEEDS_HUMAN = {
  bg: 'rgba(167,139,250,0.12)',
  color: '#A78BFA',
  border: 'rgba(167,139,250,0.42)',
};

const BulkComplianceModal = ({
  isOpen,
  onClose,
  drafts = [],
  contacts = [],
  onApply,
  onComplete,
}) => {
  const draftIds = useMemo(() => drafts.map((d) => d.config_id), [drafts]);

  const [selectedDraftIds, setSelectedDraftIds] = useState(() => new Set());
  const [verdict, setVerdict] = useState('compliant');
  const [note, setNote] = useState('');

  // Reset form to "all drafts selected, compliant verdict" each time the
  // modal opens. We don't reset on close because Apply closes the modal
  // synchronously — the next open should feel like a fresh action.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedDraftIds(new Set(draftIds));
    setVerdict('compliant');
    setNote('');
  }, [isOpen, draftIds]);

  const allDraftsChecked =
    draftIds.length > 0 && selectedDraftIds.size === draftIds.length;
  const someDraftsChecked =
    selectedDraftIds.size > 0 && selectedDraftIds.size < draftIds.length;

  const toggleDraft = useCallback((configId) => {
    setSelectedDraftIds((prev) => {
      const next = new Set(prev);
      if (next.has(configId)) next.delete(configId);
      else next.add(configId);
      return next;
    });
  }, []);

  const toggleAllDrafts = useCallback(() => {
    setSelectedDraftIds((prev) =>
      prev.size === draftIds.length ? new Set() : new Set(draftIds),
    );
  }, [draftIds]);

  const { willUpdate, willSkip } = useMemo(
    () => countBulkScope(contacts, selectedDraftIds, verdict),
    [contacts, selectedDraftIds, verdict],
  );

  const trimmedNote = note.trim();
  const noteRequired =
    verdict === 'non_compliant' || verdict === 'needs_human_review';
  const noteRequiredAndMissing =
    noteRequired && trimmedNote.length < NOTE_MIN_LEN_FOR_NON_COMPLIANT;
  const noteTooLong = note.length > NOTE_MAX_LEN;
  const canApply =
    selectedDraftIds.size > 0 &&
    willUpdate > 0 &&
    !noteRequiredAndMissing &&
    !noteTooLong;

  const handleApply = useCallback(() => {
    if (!canApply) return;
    // Close immediately — fire-and-forget. Parent's optimistic merge will
    // flip chips synchronously; the eventual toast carries the result.
    onClose();
    Promise.resolve(
      onApply({
        verdict,
        note: trimmedNote || null,
        configIds: [...selectedDraftIds],
      }),
    ).then((totals) => {
      onComplete?.(totals);
    });
  }, [
    canApply,
    onClose,
    onApply,
    onComplete,
    verdict,
    trimmedNote,
    selectedDraftIds,
  ]);

  const verdictLabel =
    verdict === 'compliant'
      ? 'Compliant'
      : verdict === 'needs_human_review'
        ? 'Needs human review'
        : 'Non-compliant';
  const applyCtaLabel =
    willUpdate > 0
      ? `Mark ${willUpdate.toLocaleString()} ${
          willUpdate === 1 ? 'cell' : 'cells'
        } ${verdictLabel.toLowerCase()}`
      : `Mark ${verdictLabel.toLowerCase()}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay bg="rgba(0,0,0,0.85)" />
      <ModalContent
        bg="#111111"
        borderColor="rgba(255,255,255,0.10)"
        borderWidth="1px"
        borderRadius="10px"
        maxW="480px"
      >
        <ModalHeader
          color="#fafafa"
          fontSize="md"
          fontWeight="600"
          letterSpacing="-0.01em"
          borderBottom="1px solid"
          borderColor="rgba(255,255,255,0.08)"
          pb={4}
        >
          <HStack spacing={2.5}>
            <Box
              p={1.5}
              borderRadius="6px"
              bg="rgba(255,255,255,0.06)"
              border="1px solid rgba(255,255,255,0.08)"
              display="flex"
              alignItems="center"
            >
              <ListChecks size={13} strokeWidth={1.75} color="rgba(255,255,255,0.7)" />
            </Box>
            <Text>Review in bulk</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton
          color="rgba(255,255,255,0.4)"
          borderRadius="full"
          _hover={{ color: 'rgba(255,255,255,0.75)', bg: 'rgba(255,255,255,0.06)' }}
        />

        <ModalBody py={5}>
          <VStack spacing={5} align="stretch">
            <Text fontSize="sm" color="#a3a3a3" lineHeight="1.7">
              Apply one verdict across every contact for the message
              drafts you select. Cells you've already reviewed
              individually are preserved; stale cells are re-stamped.
            </Text>

            <Divider borderColor="rgba(255,255,255,0.07)" />

            {/* Drafts checklist */}
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between" align="center" px={1}>
                <Text
                  fontSize="11px"
                  fontWeight="600"
                  color="rgba(255,255,255,0.4)"
                  letterSpacing="0.06em"
                  textTransform="uppercase"
                >
                  Message drafts
                </Text>
                <Text fontSize="xs" color="rgba(255,255,255,0.3)">
                  {selectedDraftIds.size} / {drafts.length} selected
                </Text>
              </HStack>

              {drafts.length === 0 ? (
                <Box py={4} textAlign="center">
                  <Text fontSize="sm" color="rgba(255,255,255,0.3)">
                    No drafts on this campaign yet.
                  </Text>
                </Box>
              ) : (
                <VStack spacing={0} align="stretch">
                  <HStack
                    spacing={3}
                    px={3}
                    py={2}
                    borderRadius="7px"
                    _hover={{ bg: 'rgba(255,255,255,0.03)' }}
                    cursor="pointer"
                    onClick={toggleAllDrafts}
                    userSelect="none"
                    mb={1}
                  >
                    <CustomCheckbox
                      checked={allDraftsChecked}
                      indeterminate={someDraftsChecked}
                      onClick={toggleAllDrafts}
                    />
                    <Text fontSize="xs" fontWeight="500" color="rgba(255,255,255,0.6)">
                      {allDraftsChecked ? 'Deselect all' : 'Select all'}
                    </Text>
                  </HStack>

                  <Divider borderColor="rgba(255,255,255,0.06)" mb={1} />

                  <Box
                    maxH="180px"
                    overflowY="auto"
                    sx={{
                      '&::-webkit-scrollbar': { w: '4px' },
                      '&::-webkit-scrollbar-thumb': {
                        bg: 'rgba(255,255,255,0.1)',
                        borderRadius: 'full',
                      },
                    }}
                  >
                    <VStack spacing={1} align="stretch">
                      {drafts.map((draft) => {
                        const checked = selectedDraftIds.has(draft.config_id);
                        return (
                          <HStack
                            key={draft.config_id}
                            spacing={3}
                            px={3}
                            py={2.5}
                            borderRadius="7px"
                            bg={checked ? 'rgba(255,255,255,0.07)' : 'transparent'}
                            border="1px solid"
                            borderColor={
                              checked
                                ? 'rgba(255,255,255,0.18)'
                                : 'rgba(255,255,255,0.04)'
                            }
                            cursor="pointer"
                            transition="all 0.12s"
                            _hover={{
                              bg: checked
                                ? 'rgba(255,255,255,0.09)'
                                : 'rgba(255,255,255,0.04)',
                              borderColor: 'rgba(255,255,255,0.18)',
                            }}
                            onClick={() => toggleDraft(draft.config_id)}
                            userSelect="none"
                          >
                            <CustomCheckbox
                              checked={checked}
                              onClick={() => toggleDraft(draft.config_id)}
                            />
                            <Text
                              fontSize="xs"
                              fontWeight="500"
                              color="#fafafa"
                              noOfLines={1}
                              flex={1}
                              minW={0}
                            >
                              {draft.display_name || 'Untitled draft'}
                            </Text>
                          </HStack>
                        );
                      })}
                    </VStack>
                  </Box>
                </VStack>
              )}
            </VStack>

            <Divider borderColor="rgba(255,255,255,0.07)" />

            {/* Verdict */}
            <VStack spacing={2} align="stretch">
              <Text
                fontSize="11px"
                fontWeight="600"
                color="rgba(255,255,255,0.4)"
                letterSpacing="0.06em"
                textTransform="uppercase"
                px={1}
              >
                Verdict
              </Text>
              <HStack spacing={2} flexWrap="wrap">
                <VerdictPill
                  active={verdict === 'compliant'}
                  onClick={() => setVerdict('compliant')}
                  icon={ShieldCheck}
                  label="Compliant"
                  accent={ACCENT_COMPLIANT}
                />
                <VerdictPill
                  active={verdict === 'needs_human_review'}
                  onClick={() => setVerdict('needs_human_review')}
                  icon={CircleHelp}
                  label="Needs human"
                  accent={ACCENT_NEEDS_HUMAN}
                />
                <VerdictPill
                  active={verdict === 'non_compliant'}
                  onClick={() => setVerdict('non_compliant')}
                  icon={X}
                  label="Non-compliant"
                  accent={ACCENT_NON_COMPLIANT}
                />
              </HStack>
            </VStack>

            {/* Note */}
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between" align="center" px={1}>
                <Text
                  fontSize="11px"
                  fontWeight="600"
                  color="rgba(255,255,255,0.4)"
                  letterSpacing="0.06em"
                  textTransform="uppercase"
                >
                  Note{' '}
                  <Text
                    as="span"
                    fontWeight="500"
                    color="rgba(255,255,255,0.3)"
                    textTransform="none"
                    letterSpacing="0"
                  >
                    {noteRequired ? '(required)' : '(optional)'}
                  </Text>
                </Text>
                <Text
                  fontSize="xs"
                  color={
                    noteTooLong ? '#EF4444' : 'rgba(255,255,255,0.3)'
                  }
                >
                  {note.length} / {NOTE_MAX_LEN}
                </Text>
              </HStack>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={
                  verdict === 'non_compliant'
                    ? 'What should the advisor change?'
                    : verdict === 'needs_human_review'
                      ? 'What must a human verify before sending?'
                      : 'Optional context for the advisor.'
                }
                bg="rgba(255,255,255,0.03)"
                border="1px solid"
                borderColor={
                  noteTooLong
                    ? 'rgba(239,68,68,0.5)'
                    : 'rgba(255,255,255,0.08)'
                }
                color="#fafafa"
                fontSize="sm"
                lineHeight="1.5"
                minH="80px"
                resize="vertical"
                _placeholder={{ color: 'rgba(255,255,255,0.3)' }}
                _hover={{ borderColor: 'rgba(255,255,255,0.18)' }}
                _focus={{
                  borderColor: 'rgba(255,255,255,0.35)',
                  boxShadow: 'none',
                }}
              />
            </VStack>

            {/* Preview strip */}
            <Box
              px={3}
              py={2.5}
              borderRadius="8px"
              bg="rgba(255,255,255,0.03)"
              border="1px solid rgba(255,255,255,0.06)"
            >
              <HStack spacing={4} justify="space-between" fontSize="xs">
                <HStack spacing={2}>
                  <Box
                    w="6px"
                    h="6px"
                    borderRadius="full"
                    bg={
                      verdict === 'compliant'
                        ? '#34D399'
                        : verdict === 'needs_human_review'
                          ? '#A78BFA'
                          : '#EF4444'
                    }
                  />
                  <Text color="rgba(255,255,255,0.75)" fontWeight="500">
                    Will update {willUpdate.toLocaleString()}{' '}
                    {willUpdate === 1 ? 'cell' : 'cells'}
                  </Text>
                </HStack>
                {willSkip > 0 && (
                  <Text color="rgba(255,255,255,0.45)">
                    Skip {willSkip.toLocaleString()} already reviewed or up-to-date
                  </Text>
                )}
              </HStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="rgba(255,255,255,0.08)" pt={4}>
          <HStack spacing={2}>
            <Button {...OUTLINE_BTN} onClick={onClose}>
              Cancel
            </Button>
            <Button
              {...PRIMARY_BTN}
              onClick={handleApply}
              isDisabled={!canApply}
            >
              {applyCtaLabel}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BulkComplianceModal;
